import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { OrgSsoOidcConfig, OrgSsoPutBody } from '../lib/orgSso';
import { createRoleMapState } from '../lib/orgSsoRoleMap';
import { Field } from './Field';
import { OrgSsoRoleMapFields } from './OrgSsoRoleMapFields';
import { Panel } from './Panel';
import { buttonClass } from './ui/Button';

// OIDC connection form (#468) — presenter only; the container (`AccountSso`)
// owns fetching + mutations. The client secret is write-only: it lives in this
// local only until PUT and sealed server-side. With a secret already sealed,
// leaving the field blank keeps it (the key is omitted from the body).
// happy-dom #481: the <form> is the LAST template child — nothing follows it.

export type OrgSsoFormProps = {
  /** Masked config to edit, or `null` for the first-connection create form. */
  readonly config: OrgSsoOidcConfig | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSave: (body: OrgSsoPutBody) => void;
};

export function OrgSsoForm(props: OrgSsoFormProps): JSX.Element {
  // Initial values only — the container remounts this form whenever server
  // truth changes (QueryBoundary re-runs its children on fresh data).
  const cfg = props.config;
  const [issuer, setIssuer] = createSignal(cfg?.issuer ?? '');
  const [clientId, setClientId] = createSignal(cfg?.client_id ?? '');
  const [secret, setSecret] = createSignal('');
  const [redirectUri, setRedirectUri] = createSignal(cfg?.redirect_uri ?? '');
  const [gateKind, setGateKind] = createSignal(cfg?.claim_gate.required_kind ?? '');
  const [gateApp, setGateApp] = createSignal(cfg?.claim_gate.required_oauth_app ?? '');
  const secretSet = cfg?.client_secret_set === true;
  const roleMap = createRoleMapState(cfg?.role_map ?? null);

  const submit = (e: Event): void => {
    e.preventDefault();
    props.onSave({
      protocol: 'oidc',
      issuer: issuer().trim(),
      client_id: clientId().trim(),
      redirect_uri: redirectUri().trim(),
      claim_gate: {
        required_kind: gateKind().trim(),
        required_oauth_app: gateApp().trim(),
      },
      // Blank secret on an existing connection = keep the sealed one (omit the key).
      ...(secret().trim() === '' ? {} : { client_secret: secret().trim() }),
      // Same keep-on-update rule: untouched rows omit the key entirely, so an
      // edit here can never wipe a map configured over the API (#1110).
      ...roleMap.bodyField(),
    });
  };

  return (
    <Panel title={t('account_sso.form_title')} class="account-sso__form-panel">
      <form class="account-sso__form" onSubmit={submit}>
        <div class="field-group account-sso__form-grid">
          <div class="account-sso__field--wide">
            <Field label={t('account_sso.issuer_label')} id="sso-issuer">
              {(a11y) => (
                <input
                  {...a11y}
                  class="input"
                  type="url"
                  value={issuer()}
                  onInput={(e) => setIssuer(e.currentTarget.value)}
                  required
                />
              )}
            </Field>
          </div>
          <Field label={t('account_sso.client_id_label')} id="sso-client-id">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                value={clientId()}
                onInput={(e) => setClientId(e.currentTarget.value)}
                required
              />
            )}
          </Field>
          <Field label={t('account_sso.client_secret_label')} id="sso-client-secret">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="password"
                autocomplete="off"
                value={secret()}
                onInput={(e) => setSecret(e.currentTarget.value)}
                placeholder={secretSet ? t('account_sso.secret_set_placeholder') : undefined}
                required={!secretSet}
              />
            )}
          </Field>
          <div class="account-sso__field--wide">
            <Field label={t('account_sso.redirect_label')} id="sso-redirect">
              {(a11y) => (
                <input
                  {...a11y}
                  class="input"
                  type="url"
                  value={redirectUri()}
                  onInput={(e) => setRedirectUri(e.currentTarget.value)}
                  required
                />
              )}
            </Field>
          </div>
          <Field label={t('account_sso.gate_kind_label')} id="sso-gate-kind">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                value={gateKind()}
                onInput={(e) => setGateKind(e.currentTarget.value)}
                required
              />
            )}
          </Field>
          <Field label={t('account_sso.gate_app_label')} id="sso-gate-app">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                value={gateApp()}
                onInput={(e) => setGateApp(e.currentTarget.value)}
                required
              />
            )}
          </Field>
        </div>
        <OrgSsoRoleMapFields state={roleMap} />
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
        <button type="submit" class={`${buttonClass()} account-sso__submit`} disabled={props.busy}>
          {props.busy ? t('account_sso.saving') : t('account_sso.save')}
        </button>
      </form>
    </Panel>
  );
}
